// client/src/components/PublicProfile/PublicProfileSearch.js
import React, { useState, useEffect, useRef } from 'react';
import { Search, User, TrendingUp, Trophy, Target, Zap, X } from 'lucide-react';
import axios from 'axios';

const PublicProfileSearch = ({ onSearch, t, placeholder }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (query.length >= 2) {
        searchUsers(query);
      } else {
        setResults([]);
        setShowResults(false);
      }
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [query]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchUsers = async (searchQuery) => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/public-profile/search/users?q=${encodeURIComponent(searchQuery)}&limit=8`);
      setResults(response.data);
      setShowResults(true);
      setSelectedIndex(-1);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      if (selectedIndex >= 0 && results[selectedIndex]) {
        onSearch(results[selectedIndex].username);
      } else {
        onSearch(query.trim());
      }
      setQuery('');
      setShowResults(false);
    }
  };

  const handleResultClick = (user) => {
    onSearch(user.username);
    setQuery('');
    setShowResults(false);
  };

  const handleKeyDown = (e) => {
    if (!showResults || results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < results.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && results[selectedIndex]) {
          handleResultClick(results[selectedIndex]);
        } else {
          handleSubmit(e);
        }
        break;
      case 'Escape':
        setShowResults(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  const clearSearch = () => {
    setQuery('');
    setResults([]);
    setShowResults(false);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  const formatLevel = (level) => {
    if (level >= 100) return { text: 'Legend', color: '#f59e0b' };
    if (level >= 90) return { text: 'Sage', color: '#8b5cf6' };
    if (level >= 80) return { text: 'Mythic', color: '#ec4899' };
    if (level >= 70) return { text: 'Grandmaster', color: '#ef4444' };
    if (level >= 60) return { text: 'Master', color: '#3b82f6' };
    if (level >= 50) return { text: 'Expert', color: '#10b981' };
    if (level >= 40) return { text: 'Pro', color: '#06b6d4' };
    if (level >= 30) return { text: 'Advanced', color: '#84cc16' };
    if (level >= 20) return { text: 'Intermediate', color: '#eab308' };
    if (level >= 10) return { text: 'Novice', color: '#f97316' };
    return { text: 'Beginner', color: '#6b7280' };
  };

  return (
    <div className="public-profile-search" ref={searchRef}>
      <form onSubmit={handleSubmit} className="search-form">
        <div className="search-input-container">
          <Search className="search-icon" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => query.length >= 2 && setShowResults(true)}
            placeholder={placeholder || t('searchPlayerPlaceholder', 'Search by username or in-game name...')}
            className="search-input"
            autoComplete="off"
          />
          {query && (
            <button
              type="button"
              onClick={clearSearch}
              className="clear-button"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <button
            type="submit"
            disabled={!query.trim()}
            className="search-button"
          >
            {t('search', 'Search')}
          </button>
        </div>
      </form>

      {/* Search Results Dropdown */}
      {showResults && (
        <div className="search-results">
          {loading ? (
            <div className="search-loading">
              <div className="loading-spinner-small"></div>
              <span>{t('searching', 'Searching...')}</span>
            </div>
          ) : results.length > 0 ? (
            <>
              <div className="search-results-header">
                <span className="results-count">
                  {results.length} {t('playersFound', 'players found')}
                </span>
              </div>
              
              <div className="search-results-list">
                {results.map((user, index) => {
                  const levelInfo = formatLevel(user.currentLevel);
                  const isSelected = index === selectedIndex;
                  
                  return (
                    <div
                      key={user._id}
                      onClick={() => handleResultClick(user)}
                      className={`search-result-item ${isSelected ? 'selected' : ''}`}
                    >
                      <div className="result-avatar">
                        <User className="w-5 h-5" />
                        <div className={`status-dot ${user.isOnline ? 'online' : 'offline'}`}></div>
                      </div>
                      
                      <div className="result-info">
                        <div className="result-names">
                          <span className="result-ingame-name">{user.inGameName}</span>
                          <span className="result-username">@{user.username}</span>
                        </div>
                        
                        <div className="result-stats">
                          <div className="result-stat">
                            <Zap className="w-3 h-3" />
                            <span 
                              className="level-text"
                              style={{ color: levelInfo.color }}
                            >
                              {t('level', 'Level')} {user.currentLevel}
                            </span>
                          </div>
                          
                          <div className="result-stat">
                            <Trophy className="w-3 h-3" />
                            <span>{user.wins} {t('wins', 'wins')}</span>
                          </div>
                          
                          <div className="result-stat">
                            <TrendingUp className="w-3 h-3" />
                            <span>{user.winRate}%</span>
                          </div>
                          
                          <div className="result-stat">
                            <Target className="w-3 h-3" />
                            <span>{user.totalExp?.toLocaleString() || 0} EXP</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="result-status">
                        <span className={`status-text ${user.isOnline ? 'online' : 'offline'}`}>
                          {user.isOnline ? t('online', 'Online') : t('offline', 'Offline')}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <div className="search-results-footer">
                <span className="search-tip">
                  {t('searchTip', 'Use ↑↓ to navigate, Enter to select, Esc to close')}
                </span>
              </div>
            </>
          ) : query.length >= 2 ? (
            <div className="no-results">
              <User className="w-8 h-8 text-gray-400 mb-2" />
              <span className="no-results-text">
                {t('noPlayersFound', 'No players found matching "{query}"')
                  .replace('{query}', query)}
              </span>
              <span className="no-results-tip">
                {t('searchTipExact', 'Try searching with the exact username or in-game name')}
              </span>
            </div>
          ) : null}
        </div>
      )}

      {/* Search Tips */}
      <div className="search-tips">
        <div className="search-tip-item">
          <Search className="w-3 h-3" />
          <span>{t('searchTipUsername', 'Search by username (e.g., @player123)')}</span>
        </div>
        <div className="search-tip-item">
          <User className="w-3 h-3" />
          <span>{t('searchTipInGame', 'Search by in-game name (e.g., ProGamer)')}</span>
        </div>
      </div>
    </div>
  );
};

export default PublicProfileSearch;